import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, FileText, Mail, Share2, Subtitles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TourPlayer } from "@/components/TourPlayer";
import { ShareTour } from "@/components/ShareTour";
import { useI18n } from "@/context/I18nContext";
import { API, api } from "@/lib/api";

const WHOLE = "ChatMiLu — chat, message, read the news and plan your week in any of 50 languages.";

export default function Tour() {
  const { lang } = useI18n();
  // a friend arriving on a shared tour keeps the code, so signing up still earns both of them days
  const fromFriend = (new URLSearchParams(window.location.search).get("ref") || "").toUpperCase();
  const [copied, setCopied] = useState(false);
  const [scene, setScene] = useState(null);   // what MiLu is showing right now
  const [say, setSay] = useState({});         // the page's words, in this language

  useEffect(() => {
    api.get("/tour", { params: { lang } }).then(({ data }) => setSay(data.ui || {})).catch(() => {});
  }, [lang]);
  // a shared link in another language goes through a page that carries that language's own
  // picture and words, so the preview unfurls translated instead of in English
  const chapter = scene && scene.key !== "open" && scene.key !== "close" ? scene.key : "";
  const link = lang === "en" && !chapter
    ? `${window.location.origin}/tour`
    : `${window.location.origin}/api/tour/link/${lang}${chapter ? `?scene=${chapter}` : ""}`;
  const onScene = useCallback((next) => setScene(next), []);

  useEffect(() => {
    document.title = "The 60-second tour · ChatMiLu";
  }, []);

  const LINE = chapter ? `ChatMiLu — ${scene.caption}` : WHOLE;

  const open = (url) => window.open(url, "_blank", "noopener,width=640,height=680");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${LINE} ${link}`);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Your browser would not let me copy that");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "The ChatMiLu tour", text: LINE, url: link });
        return;
      } catch {
        return;  // the sheet was dismissed, nothing to say
      }
    }
    copy();
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8" data-testid="tour-page">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">
        {say.watch_first || "Watch first"}
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold leading-tight sm:text-5xl">
        {say.everything || "Everything ChatMiLu does, in 60 seconds"}
      </h1>
      <p className="mt-3 max-w-2xl text-base text-ink-soft">
        {say.intro || "MiLu walks you through the whole app and narrates it in your own language."}
      </p>

      <div className="mt-8">
        <TourPlayer onScene={onScene} />
      </div>

      <p className="mt-4 text-xs text-ink-soft" data-testid="tour-share-what">
        {chapter
          ? `${say.sharing_one || "Sharing this chapter"}: “${scene.title}” — ${say.opens_on_it || "the link opens straight on it"}`
          : (say.sharing_all || "Sharing the whole tour — start a chapter to share just that one")}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="tour-share-row">
        <Button
          data-testid="tour-share"
          onClick={share}
          className="min-h-[44px] rounded-full bg-brand px-5 hover:bg-brand-hover"
        >
          <Share2 className="me-2 h-4 w-4" />
          {chapter ? (say.share_one || "Share this chapter") : (say.share_all || "Share the tour")}
        </Button>
        <Button
          data-testid="tour-share-copy"
          variant="outline"
          onClick={copy}
          className="min-h-[44px] rounded-full px-5"
        >
          {copied ? <Check className="me-2 h-4 w-4 text-forest" /> : <Copy className="me-2 h-4 w-4" />}
          Copy link
        </Button>
        <Button
          data-testid="tour-share-whatsapp"
          variant="outline"
          onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${LINE} ${link}`)}`)}
          className="min-h-[44px] rounded-full px-5"
        >
          WhatsApp
        </Button>
        <Button
          data-testid="tour-share-x"
          variant="outline"
          onClick={() => open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(LINE)}&url=${encodeURIComponent(link)}`)}
          className="min-h-[44px] rounded-full px-5"
        >
          Post on X
        </Button>
        <Button
          data-testid="tour-share-facebook"
          variant="outline"
          onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`)}
          className="min-h-[44px] rounded-full px-5"
        >
          Facebook
        </Button>
        <Button
          data-testid="tour-share-email"
          variant="outline"
          onClick={() => {
            window.location.href =
              `mailto:?subject=${encodeURIComponent("The ChatMiLu tour")}&body=${encodeURIComponent(`${LINE}\n\n${link}`)}`;
          }}
          className="min-h-[44px] rounded-full px-5"
        >
          <Mail className="me-2 h-4 w-4" /> Email
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="tour-script-row">
        <span className="text-xs text-ink-soft">{say.words_q || "Need the words?"}</span>
        <a
          data-testid="tour-script-text"
          href={`${API}/tour/script.txt?lang=${lang}`}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          <FileText className="h-3.5 w-3.5" /> {say.script_dl || "Download the script"}
        </a>
        <a
          data-testid="tour-script-srt"
          href={`${API}/tour/script.srt?lang=${lang}`}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          <Subtitles className="h-3.5 w-3.5" /> Subtitles (.srt)
        </a>
        <Link
          to="/press"
          data-testid="tour-press-link"
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          {say.for_press || "For press — screens, logos and facts"}
        </Link>
      </div>

      <div className="mt-8">
        <ShareTour />
      </div>

      <div className="mt-10 rounded-3xl border border-white/10 bg-panel p-6">
        <p className="text-base font-semibold">Ready to try it yourself?</p>
        <p className="mt-1 text-sm text-ink-soft">
          Free to start. No card, no setup — just pick your language and say hello.
        </p>
        <Link to={fromFriend ? `/signup?ref=${fromFriend}` : "/signup"}>
          <Button
            data-testid="tour-signup"
            className="mt-4 min-h-[46px] rounded-full bg-brand px-6 hover:bg-brand-hover"
          >
            Start free <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
